import React from "react";
import { useT } from "@/context/LangContext";
import { useBrand } from "@/context/BrandContext";
import { Gift, ExternalLink, Sparkles } from "lucide-react";

/**
 * Network referral advantage block.
 * Shown on B2B (after Pricing) and B2C (after Value Counter) pages.
 *
 * Visitors who arrive via a partner merchant get -X% on the annual subscription
 * of the host center. All numbers/labels are CMS-driven (brand.referral_*).
 *
 * Variants:
 *  - "b2b": angled to the merchant ("vos clients deviennent membres avec une remise")
 *  - "b2c": angled to the visitor ("profitez de votre remise via votre commerçant")
 */
export default function ReferralCallout({ variant = "b2c" }) {
  const { t, lang } = useT();
  const { brand } = useBrand();

  if (!brand?.referral_enabled) return null;

  const pct = brand.referral_discount_pct ?? 6;
  const adultSave = brand.referral_saving_adult ?? 42;
  const juniorSave = brand.referral_saving_junior ?? 36;
  const period = (lang === "nl" ? brand.referral_period_label_nl : brand.referral_period_label_fr) || (lang === "nl" ? "jaarlijks" : "annuel");
  const url = brand.referral_url || "https://www.bhealth.be";
  const center = brand.center_short || "B.health®";

  // Title (CMS override > i18n default)
  const titleOverride = lang === "nl" ? brand.referral_title_nl : brand.referral_title_fr;
  const i18nVars = { center, pct, period };
  const title = titleOverride
    || (variant === "b2b" ? t(`referral.b2b.title`, i18nVars) : t(`referral.b2c.title`, i18nVars));

  const subtitle = variant === "b2b"
    ? t("referral.b2b.subtitle", i18nVars)
    : t("referral.b2c.subtitle", i18nVars);

  const noteOverride = lang === "nl" ? brand.referral_note_nl : brand.referral_note_fr;
  const note = noteOverride || t("referral.note", i18nVars);

  const adultLabel = (lang === "nl" ? brand.plan_adult_label_nl : brand.plan_adult_label_fr) || "Adult";
  const juniorLabel = (lang === "nl" ? brand.plan_junior_label_nl : brand.plan_junior_label_fr) || "Junior/Senior";

  return (
    <section
      className="section-pad bg-white"
      data-testid={`referral-callout-${variant}`}
    >
      <div className="container-app">
        <div
          className="relative rounded-3xl overflow-hidden p-8 sm:p-10 md:p-14 border border-navy-900/10 shadow-xl"
          style={{
            background:
              "radial-gradient(circle at 0% 0%, rgba(191,255,0,0.18), transparent 55%), radial-gradient(circle at 100% 100%, rgba(11,18,32,0.06), transparent 55%), #ffffff",
          }}
        >
          {/* Accent strip */}
          <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: "var(--brand-accent, #BFFF00)" }} />

          <div className="grid md:grid-cols-12 gap-8 items-center">
            {/* LEFT — message */}
            <div className="md:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 chip bg-lime-500/15 text-navy-900 font-bold uppercase tracking-widest text-[11px]">
                <Sparkles className="w-3.5 h-3.5" />
                {t("referral.kicker")}
              </div>

              <h2 className="font-heading font-extrabold text-navy-900 text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight">
                {title}
              </h2>

              <p className="text-navy-900/70 text-base md:text-lg leading-relaxed max-w-2xl">
                {subtitle}
              </p>

              {variant === "b2c" && (
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 mt-2 px-6 py-3 rounded-full font-bold text-sm transition shadow-md hover:shadow-lg"
                  style={{ background: "var(--brand-primary, #0b1220)", color: "var(--brand-accent, #BFFF00)" }}
                  data-testid="referral-cta-b2c"
                >
                  {t("referral.cta_b2c")}
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>

            {/* RIGHT — savings card */}
            <div className="md:col-span-5">
              <div
                className="rounded-2xl p-6 text-white relative overflow-hidden"
                style={{ background: "var(--brand-primary, #0b1220)" }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: "var(--brand-accent, #BFFF00)" }}
                  >
                    <Gift className="w-5 h-5" style={{ color: "var(--brand-primary, #0b1220)" }} />
                  </div>
                  <div className="text-[11px] uppercase tracking-widest font-bold opacity-80">
                    {t("referral.discount_label", i18nVars)}
                  </div>
                </div>

                <div className="space-y-3">
                  <SaveRow
                    label={`${adultLabel} · ${t("referral.annual", i18nVars)}`}
                    amount={adultSave}
                  />
                  <div className="border-t border-white/10" />
                  <SaveRow
                    label={`${juniorLabel} · ${t("referral.annual", i18nVars)}`}
                    amount={juniorSave}
                  />
                </div>

                <div className="mt-5 text-[11px] text-white/60 leading-relaxed italic">
                  {note}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SaveRow({ label, amount }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div className="text-sm text-white/80 truncate">{label}</div>
      <div className="font-heading font-black text-2xl md:text-3xl whitespace-nowrap" style={{ color: "var(--brand-accent, #BFFF00)" }}>
        −{amount}€
      </div>
    </div>
  );
}
